/**
 * 03 · Qué mueve la aguja: la línea elegida en el waterfall abierta en barras
 * horizontales ordenadas por magnitud desde un cero central (nunca una torta:
 * la pregunta es quién movió la aguja, no qué porcentaje del total es cada uno).
 * Las líneas de costo se abren en non-payroll (default), payroll o total; las
 * subcategorías se recortan a las 8 que más mueven y cada grupo arranca
 * plegado: se abre al tocar el nombre. Debajo, el impacto FX por
 * sociedad: lectura transversal, aparte del puente.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronRight } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { computeDrivers, computeFx, DRIVER_SUB_LIMIT, type PnlBase, type PnlDriverGroup, type PnlPeriod } from '../../../../../shared/finance-pnl';
import type { PnlDashboardData } from '../../../../../shared/types/finance-pnl';
import { C, deltaColor, usePayOptions, type PayMode, type PnlFmt } from './pnl-format';
import { PnlNote, SectionHead, Seg, SegLabel } from './pnl-ui';
import type { WaterfallKey } from './PnlWaterfall';

const LINE_KEY: Record<PnlDriverGroup, string> = {
  revenue: 'budget_control.lines.revenue',
  cogs: 'budget_control.lines.cogs',
  gya: 'budget_control.lines.gya',
  investment: 'budget_control.lines.investment',
};

interface TornadoRow {
  label: string;
  value: number;
  sub?: TornadoRow[];
}

/**
 * Barras desde un cero central. Cada grupo arranca plegado y se abre al tocar
 * el nombre (Sol 01/10): así no se ve todo el detalle de una, sino que cada
 * lector expande lo que le interesa. La escala se calcula sobre todas las
 * filas, abiertas o no, para que las barras no salten al expandir.
 */
function Tornado({ rows, worsePos, fmt }: { rows: TornadoRow[]; worsePos: boolean; fmt: PnlFmt }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState<Set<string>>(() => new Set());
  const expandable = rows.filter((r) => r.sub && r.sub.length > 0).map((r) => r.label);
  const allOpen = expandable.length > 0 && expandable.every((l) => open.has(l));
  const toggle = (label: string) =>
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(label)) next.delete(label);
      else next.add(label);
      return next;
    });
  const mx = Math.max(...rows.flatMap((r) => [Math.abs(r.value), ...(r.sub ?? []).map((x) => Math.abs(x.value))]), 1);

  const line = (r: TornadoRow, sub: boolean, i: string) => {
    const worse = worsePos ? r.value > 0 : r.value < 0;
    const col = deltaColor(!worse, r.value);
    const w = Math.max((Math.abs(r.value) / mx) * 49, 0.6);
    const canOpen = !sub && !!r.sub && r.sub.length > 0;
    const isOpen = canOpen && open.has(r.label);
    const name = canOpen ? (
      <button
        type="button"
        onClick={() => toggle(r.label)}
        aria-expanded={isOpen}
        className="inline-flex max-w-full items-center justify-end gap-1 truncate text-right text-xs text-foreground hover:underline"
        title={r.label}
      >
        <span className="truncate">{r.label}</span>
        <span className="text-[10px] text-muted-foreground">({r.sub!.length})</span>
        <ChevronRight className={cn('h-3 w-3 flex-none text-muted-foreground transition-transform', isOpen && 'rotate-90')} />
      </button>
    ) : (
      <div className={cn('truncate text-right text-xs text-muted-foreground', sub && 'text-[11.5px] opacity-80')} title={r.label}>
        {sub ? `↳ ${r.label}` : r.label}
      </div>
    );
    return (
      <div key={i} className="grid grid-cols-[130px_1fr_74px] items-center gap-2.5 py-1 md:grid-cols-[190px_1fr_88px]">
        <div className="flex justify-end">{name}</div>
        <div className="relative h-[17px]">
          <span className="absolute bottom-[-3px] top-[-3px] left-1/2 w-px" style={{ background: C.hairStrong }} />
          <span
            className="absolute top-[2px] h-[13px] rounded-[3px]"
            style={{ background: col, opacity: sub ? 0.55 : 1, width: `${w}%`, ...(r.value >= 0 ? { left: '50%' } : { right: '50%' }) }}
          />
        </div>
        <div className={cn('text-right font-mono text-xs font-semibold tabular-nums', sub && 'opacity-80')} style={{ color: col }}>
          {fmt.sgnK(r.value)}
        </div>
      </div>
    );
  };

  return (
    <div>
      {expandable.length > 0 && (
        <div className="mb-1 flex justify-end">
          <button
            type="button"
            onClick={() => setOpen(allOpen ? new Set() : new Set(expandable))}
            className="text-[11px] text-muted-foreground hover:text-foreground hover:underline"
          >
            {allOpen ? t('budget_control.drivers.collapse_all') : t('budget_control.drivers.expand_all')}
          </button>
        </div>
      )}
      {rows.map((r, i) => (
        <div key={`${i}-${r.label}`}>
          {line(r, false, `p${i}`)}
          {open.has(r.label) && (r.sub ?? []).map((x, j) => line(x, true, `s${i}-${j}`))}
        </div>
      ))}
    </div>
  );
}

export function PnlDrivers({
  data,
  group,
  month,
  period,
  base,
  periodLabel,
  fmt,
  notes,
}: {
  data: PnlDashboardData;
  group: WaterfallKey;
  month: number;
  period: PnlPeriod;
  base: PnlBase;
  periodLabel: string;
  fmt: PnlFmt;
  notes: boolean;
}) {
  const { t } = useTranslation();
  const pay$ = usePayOptions();
  const [pay, setPay] = useState<PayMode>('nonPayroll');
  const isSubtotal = group === 'start' || group === 'end';
  const isFlat = group === 'bonos' || group === 'taxes';
  const isCost = group === 'cogs' || group === 'gya' || group === 'investment';
  const drivers = !isSubtotal && !isFlat ? computeDrivers(data, group, month, period, base, pay) : null;
  const fx = computeFx(data, month);
  const fxTotal = fx.reduce((s, f) => s + f.month, 0);
  const fxYtd = fx.reduce((s, f) => s + f.ytd, 0);
  const fxMax = Math.max(...fx.map((f) => Math.abs(f.month)), 1);

  const title = isSubtotal
    ? t(group === 'start' ? 'budget_control.waterfall.start' : 'budget_control.waterfall.end')
    : isFlat
      ? t(group === 'bonos' ? 'budget_control.lines.bonos' : 'budget_control.lines.taxes')
      : `${t(LINE_KEY[group as PnlDriverGroup])}${group === 'revenue' ? '' : ` ${pay$.longLabel(drivers!.pay)}`} · ${fmt.k(drivers!.actual)}`;

  const badge = drivers
    ? drivers.base === 'prev'
      ? t('budget_control.drivers.vs_prev', { month: fmt.monthLong(month), prev: fmt.monthLong(month - 1) })
      : t('budget_control.drivers.vs_budget', { period: periodLabel })
    : '';

  // El payroll viene como la categoría 'S&B': en pantalla se dice que es un agregado.
  const subLabel = (l: string) => l.replace(/^S&B(?= · |$)/, pay$.sbLabel);
  const rows: TornadoRow[] = drivers
    ? drivers.items.map((it) => ({ label: it.label, value: it.value, sub: (it.sub ?? []).map((s) => ({ label: subLabel(s.label), value: s.value })) }))
    : [];

  return (
    <section>
      <SectionHead num="03" title={t('budget_control.drivers.title')} sub={t('budget_control.drivers.subtitle')} />
      <Card className="p-4 shadow-xs">
        <div className="mb-3.5 flex flex-wrap items-center gap-2.5">
          <span className="text-[15px] font-semibold">{title}</span>
          {drivers && (
            <span
              className="rounded-md border px-1.5 py-px text-[10.5px] font-semibold uppercase tracking-[0.06em]"
              style={drivers.base === 'prev' ? { color: C.warn, borderColor: C.warn } : { background: C.surface2, color: C.ink2, borderColor: C.hair }}
            >
              {badge}
            </span>
          )}
          {drivers && (
            <span className="text-xs text-muted-foreground">
              {t('budget_control.drivers.total_line', {
                delta: fmt.sgnK(drivers.actual - drivers.ref),
                ref: drivers.base === 'prev' ? fmt.monthLong(month - 1) : t('budget_control.pnl.budget'),
              })}
            </span>
          )}
          {isCost && (
            <>
              <span className="flex-1" />
              <SegLabel>{t('budget_control.pay.label')}</SegLabel>
              <Seg<PayMode> ariaLabel={t('budget_control.pay.label')} value={pay} onChange={setPay} options={pay$.options} />
            </>
          )}
        </div>
        {isSubtotal || isFlat || rows.length === 0 ? (
          <div className="rounded-lg border border-dashed border-border px-4 py-6 text-center text-sm text-muted-foreground">
            <b className="mb-1 block text-sm text-foreground">
              {isSubtotal
                ? t('budget_control.drivers.empty_subtotal_title')
                : isFlat
                  ? t('budget_control.drivers.empty_flat_title')
                  : t('budget_control.drivers.empty_none_title')}
            </b>
            {isSubtotal
              ? t('budget_control.drivers.empty_subtotal_text')
              : isFlat
                ? t('budget_control.drivers.empty_flat_text')
                : t('budget_control.drivers.empty_none_text')}
          </div>
        ) : (
          <Tornado key={`${group}-${pay}-${drivers!.base}`} rows={rows} worsePos={drivers!.worsePos} fmt={fmt} />
        )}
      </Card>

      <Card className="mt-3.5 p-4 shadow-xs">
        <div className="mb-3.5 flex flex-wrap items-center gap-2.5">
          <span className="text-[15px] font-semibold">{t('budget_control.fx.title')}</span>
          <span className="rounded-md border border-border px-1.5 py-px text-[10.5px] font-semibold uppercase tracking-[0.06em] text-muted-foreground" style={{ background: C.surface2 }}>
            {fmt.monthLong(month)}
          </span>
          <span className="text-xs text-muted-foreground">
            {t('budget_control.fx.total', { month: fmt.k(fxTotal), monthName: fmt.monthLong(month), ytd: fmt.k(fxYtd) })}
          </span>
        </div>
        {fx.length === 0 ? (
          <div className="rounded-lg border border-dashed border-border px-4 py-6 text-center text-sm text-muted-foreground">{t('budget_control.fx.empty')}</div>
        ) : (
          <div>
            <div className="grid grid-cols-[70px_1fr_66px_66px] gap-2.5 px-2 pb-1.5 text-[10px] font-semibold uppercase tracking-[0.06em] text-muted-foreground">
              <span>{t('budget_control.fx.company')}</span>
              <span />
              <span className="text-right">{fmt.monthShort(month)}</span>
              <span className="text-right">{t('budget_control.fx.ytd', { from: fmt.monthShort(1), to: fmt.monthShort(month) })}</span>
            </div>
            {fx.map((f) => {
              const col = deltaColor(f.month >= 0, f.month);
              const w = Math.max((Math.abs(f.month) / fxMax) * 49, 0.6);
              return (
                <div key={f.company} className="grid grid-cols-[70px_1fr_66px_66px] items-center gap-2.5 px-2 py-1.5">
                  <span className="text-xs">{f.company}</span>
                  <div className="relative h-[15px]">
                    <span className="absolute bottom-[-2px] top-[-2px] left-1/2 w-px" style={{ background: C.hairStrong }} />
                    <span className="absolute top-[2px] h-[11px] rounded-[2px]" style={{ background: col, width: `${w}%`, ...(f.month >= 0 ? { left: '50%' } : { right: '50%' }) }} />
                  </div>
                  <span className="text-right font-mono text-xs font-semibold tabular-nums" style={{ color: col }}>{fmt.sgnK(f.month)}</span>
                  <span className="text-right font-mono text-xs font-semibold tabular-nums text-muted-foreground">{fmt.sgnK(f.ytd)}</span>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      <PnlNote
        show={notes}
        items={[
          { tag: 'form', title: t('budget_control.notes.drivers.form_title'), text: t('budget_control.notes.drivers.form_text') },
          { tag: 'source', title: t('budget_control.notes.drivers.source_title'), text: t('budget_control.notes.drivers.source_text') },
          { tag: 'new', title: t('budget_control.notes.drivers.base_title'), text: t('budget_control.notes.drivers.base_text') },
          { tag: 'new', title: t('budget_control.notes.drivers.payroll_title'), text: t('budget_control.notes.drivers.payroll_text') },
          { tag: 'rule', title: t('budget_control.notes.drivers.cut_title', { n: DRIVER_SUB_LIMIT }), text: t('budget_control.notes.drivers.cut_text', { n: DRIVER_SUB_LIMIT }) },
          { tag: 'warn', title: t('budget_control.notes.drivers.reconcile_title'), text: t('budget_control.notes.drivers.reconcile_text') },
          { tag: 'location', title: t('budget_control.notes.drivers.location_title'), text: t('budget_control.notes.drivers.location_text') },
          { tag: 'decision', title: t('budget_control.notes.drivers.decision_title'), text: t('budget_control.notes.drivers.decision_text') },
        ]}
      />
    </section>
  );
}
